import React, { useEffect, useState, createElement } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon, CopyIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { FormBuilder } from '../components/builder/FormBuilder';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME, LANDING_PAGE_PALETTE } from '../components/builder/types';
import { landingPagesApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
function toSlug(str: string): string {
  return str.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}
export function LandingPageEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // FIX: Team is always the workspace name, Region is the sub-region
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  const [pageName, setPageName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [status, setStatus] = useState('active');
  const [pageData, setPageData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited && !isEditing) {
      setUrlSlug(toSlug(pageName));
    }
  }, [pageName, slugManuallyEdited, isEditing]);
  // Load existing page when editing
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      landingPagesApi.get(id).then(record => {
        if (record) {
          setPageName(record.Page_Name || '');
          setUrlSlug(record.Url_Slug || '');
          setSlugManuallyEdited(true);
          setStatus(record.Status || 'active');
          setPageData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          // Restore builder state if available
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              setEditorMode('builder');
            } catch {
              /* fallback to code mode */
            }
          } else if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
            } catch {
              /* ignore */
            }
          }
        } else {
          toast.error('Landing page not found');
          navigate('/landing-pages');
        }
      }).catch(err => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load landing page', {
          description: msg
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!pageName.trim()) {
      toast.error('Page Name is required');
      return;
    }
    if (!urlSlug.trim()) {
      toast.error('URL Slug is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Page_Name: pageName,
      Url_Slug: urlSlug,
      Status: status,
      HTML: pageData.html,
      CSS: pageData.css,
      JavaScript1: pageData.js,
      Team: team,
      Region: region,
      Builder_Mode: editorMode
    };
    // Save builder schema
    if (builderBlocks.length > 0) {
      payload.Builder_Schema = JSON.stringify({
        blocks: builderBlocks,
        theme: builderTheme
      });
    } else if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: [],
        theme: builderTheme
      });
    }
    try {
      if (isEditing && id) {
        await landingPagesApi.update(id, payload);
        toast.success('Landing page updated', {
          description: `"${pageName}" has been updated.`
        });
      } else {
        await landingPagesApi.create(payload);
        toast.success('Landing page created', {
          description: `"${pageName}" has been saved.`
        });
      }
      if (!isEditing) {
        navigate('/landing-pages');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save landing page', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const publicUrl = urlSlug ? `${window.location.origin}/page/${urlSlug}` : '';
  const handleCopyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = publicUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4591">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4592" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4593">
          Loading landing page...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4594">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4595">
        <div className="flex items-center gap-4" data-id="element-4596">
          <Link to="/landing-pages" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4597">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-4598" />
          </Link>
          <div data-id="element-4599">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4600">
              {isEditing ? 'Edit Page: ' + (pageName || '...') : 'Create Landing Page'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-4601">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your page.' : 'Build your landing page with custom HTML, CSS, and JS.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-4602">
          {/* Mode Toggle */}
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-4603">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4604">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && pageData.html && builderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4605">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4606">
            <EyeIcon className="h-4 w-4" data-id="element-4607" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4608">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4609" /> : <SaveIcon className="h-4 w-4" data-id="element-4610" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-4611">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} hideLogicRules customPalette={LANDING_PAGE_PALETTE} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setPageData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-4612" />
          {/* Settings below builder */}
          <div className="grid grid-cols-3 gap-6" data-id="element-4613">
            <div className="col-span-2" data-id="element-4614" />
            <div className="space-y-6" data-id="element-4615">
              <SettingsCard pageName={pageName} setPageName={setPageName} urlSlug={urlSlug} setUrlSlug={setUrlSlug} slugManuallyEdited={slugManuallyEdited} setSlugManuallyEdited={setSlugManuallyEdited} status={status} setStatus={setStatus} team={team} region={region} isEditing={isEditing} data-id="element-4616" />
              <PublicUrlCard publicUrl={publicUrl} urlSlug={urlSlug} status={status} copiedUrl={copiedUrl} onCopy={handleCopyUrl} data-id="element-4617" />
            </div>
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-4618">
          <div className="col-span-2 space-y-6" data-id="element-4619">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-4620">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-4621">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={'text-sm font-medium px-3 py-1.5 rounded-md transition-colors ' + (activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50')} data-id="element-4622">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-4623">
                {activeTab === 'html' && <CodeEditor label="Page HTML" language="html" placeholder="<div class='landing-page'>...</div>" value={pageData.html} onChange={e => setPageData({
              ...pageData,
              html: e.target.value
            })} data-id="element-4624" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".landing-page { ... }" value={pageData.css} onChange={e => setPageData({
              ...pageData,
              css: e.target.value
            })} data-id="element-4625" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="// Add interactivity..." value={pageData.js} onChange={e => setPageData({
              ...pageData,
              js: e.target.value
            })} data-id="element-4626" />}
              </div>
            </div>

            {/* GPT Code Assistant */}
            <GptCodeAssistant mode="form" currentCode={pageData} onCodeGenerated={code => {
          setPageData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-4627" />
          </div>

          <div className="space-y-6" data-id="element-4628">
            <SettingsCard pageName={pageName} setPageName={setPageName} urlSlug={urlSlug} setUrlSlug={setUrlSlug} slugManuallyEdited={slugManuallyEdited} setSlugManuallyEdited={setSlugManuallyEdited} status={status} setStatus={setStatus} team={team} region={region} isEditing={isEditing} data-id="element-4629" />
            <PublicUrlCard publicUrl={publicUrl} urlSlug={urlSlug} status={status} copiedUrl={copiedUrl} onCopy={handleCopyUrl} data-id="element-4630" />

            {/* Tips Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4631">
              <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4632">
                How to use
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2 text-xs text-blue-700" data-id="element-4633">
                <p data-id="element-4634">
                  <strong data-id="element-4635">1.</strong> Build your landing page using the code
                  editor or builder
                </p>
                <p data-id="element-4636">
                  <strong data-id="element-4637">2.</strong> Set the status to <strong data-id="element-4638">Active</strong>{' '}
                  and save
                </p>
                <p data-id="element-4639">
                  <strong data-id="element-4640">3.</strong> Go to{' '}
                  <strong data-id="element-4641">Email Templates → Edit</strong> and paste the public
                  URL into the <strong data-id="element-4642">"Redirect After Confirmation"</strong>{' '}
                  field
                </p>
                <p data-id="element-4643">
                  <strong data-id="element-4644">4.</strong> When subscribers confirm, they'll be
                  redirected to your custom landing page
                </p>
              </div>
              <p className="text-xs text-gray-500" data-id="element-4645">
                You can also select a landing page directly from the email
                template editor's redirect dropdown.
              </p>
            </div>
          </div>
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Landing Page Preview" data-id="element-4646">
        <FormPreview html={pageData.html} css={pageData.css} js={pageData.js} data-id="element-4647" />
      </PreviewModal>

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-4648" />
    </div>;
}
// ── Settings Card ──
function SettingsCard({
  pageName,
  setPageName,
  urlSlug,
  setUrlSlug,
  slugManuallyEdited,
  setSlugManuallyEdited,
  status,
  setStatus,
  team,
  region,
  isEditing
}: {
  pageName: string;
  setPageName: (v: string) => void;
  urlSlug: string;
  setUrlSlug: (v: string) => void;
  slugManuallyEdited: boolean;
  setSlugManuallyEdited: (v: boolean) => void;
  status: string;
  setStatus: (v: string) => void;
  team: string;
  region: string;
  isEditing: boolean;
}) {
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4649">
      <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4650">
        Settings
      </h3>
      <div className="space-y-2" data-id="element-4651">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4652">
          Page Name
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={pageName} onChange={e => setPageName(e.target.value)} placeholder="Thank You Page" data-id="element-4653" />
      </div>
      <div className="space-y-2" data-id="element-4654">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4655">
          URL Slug
        </label>
        <div className="flex rounded-md shadow-sm" data-id="element-4656">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-4657">
            /page/
          </span>
          <input type="text" className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={urlSlug} onChange={e => {
          setUrlSlug(e.target.value);
          setSlugManuallyEdited(true);
        }} placeholder="thank-you" data-id="element-4658" />
        </div>
        {!slugManuallyEdited && !isEditing && pageName && <p className="text-xs text-gray-400" data-id="element-4659">Auto-generated from page name</p>}
      </div>
      <div className="space-y-2" data-id="element-4660">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4661">
          Status
        </label>
        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={status} onChange={e => setStatus(e.target.value)} data-id="element-4662">
          <option value="active" data-id="element-4663">Active</option>
          <option value="draft" data-id="element-4664">Draft</option>
          <option value="archived" data-id="element-4665">Archived</option>
        </select>
      </div>
      <div className="space-y-2" data-id="element-4666">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4667">Team</label>
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4668" />
        <p className="text-xs text-gray-400" data-id="element-4669">
          Automatically set from your current workspace
        </p>
      </div>
      <div className="space-y-2" data-id="element-4670">
        <label className="block text-sm font-medium text-gray-700" data-id="element-4671">
          Region
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={region || '(All regions)'} disabled data-id="element-4672" />
        <p className="text-xs text-gray-400" data-id="element-4673">
          Automatically set from your current scope selection
        </p>
      </div>
    </div>;
}
// ── Public URL Card ──
function PublicUrlCard({
  publicUrl,
  urlSlug,
  status,
  copiedUrl,
  onCopy
}: {
  publicUrl: string;
  urlSlug: string;
  status: string;
  copiedUrl: boolean;
  onCopy: () => void;
}) {
  if (!urlSlug) return null;
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4674">
      <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4675">
        Public URL
      </h3>
      <div className="flex items-center gap-2" data-id="element-4676">
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-600" value={publicUrl} readOnly onClick={e => (e.target as HTMLInputElement).select()} data-id="element-4677" />
        <button onClick={onCopy} className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors flex-shrink-0" data-id="element-4678">
          {copiedUrl ? <CheckIcon className="h-3.5 w-3.5" data-id="element-4679" /> : <CopyIcon className="h-3.5 w-3.5" data-id="element-4680" />}
          {copiedUrl ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {status?.toLowerCase() !== 'active' && <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-4681">
          <svg className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4682">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-4683" />
          </svg>
          <p className="text-xs text-amber-800" data-id="element-4684">
            This page is not active. Set status to <strong data-id="element-4685">Active</strong> for
            it to be publicly accessible.
          </p>
        </div>}
      {status?.toLowerCase() === 'active' && <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-4686">
          <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-4687" />
          Open in new tab →
        </a>}
    </div>;
}